import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';

type Company = {
  company_id: string;
  name: string;
  aliases?: string[] | null;
  ticker?: string | null;
};

type Flag = {
  id: number;
  kind?: string | null;
  trial_id?: string | null;
  source_id?: string | null;
  raw_name: string;
  suggested_company_id?: string | null;
  trial_name?: string | null;
  nct_id?: string | null;
  source_name?: string | null;
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);
}

const ManageReadoutCompanies: React.FC = () => {
  const [flags, setFlags] = useState<Flag[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [createOpen, setCreateOpen] = useState<Record<number, boolean>>({});
  const [createName, setCreateName] = useState<Record<number, string>>({});
  const [createId, setCreateId] = useState<Record<number, string>>({});
  const [createTicker, setCreateTicker] = useState<Record<number, string>>({});

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.fetchAug2026ReadoutTracker();
      if (data?.error) throw new Error(data.error);
      setFlags(data?.unmatched_companies || []);
      setCompanies(data?.companies || []);
      const nextDraft: Record<number, string> = {};
      for (const flag of data?.unmatched_companies || []) {
        if (flag.suggested_company_id) nextDraft[flag.id] = flag.suggested_company_id;
      }
      setDraft(nextDraft);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load unmatched companies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const companyOptions = useMemo(
    () => [...companies].sort((a, b) => a.name.localeCompare(b.name)),
    [companies],
  );

  const resolve = async (flag: Flag, payload: Parameters<typeof apiService.resolveAug2026ReadoutCompanyFlag>[1]) => {
    try {
      setSavingId(flag.id);
      setError(null);
      const res = await apiService.resolveAug2026ReadoutCompanyFlag(flag.id, payload);
      if (res?.error) throw new Error(res.error);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4">
        <h1 className="text-xl font-bold text-slate-900">Unmatched readout companies</h1>
        <p className="text-sm text-slate-500 mt-1">
          Assign a sponsor or IR source to an <strong>existing</strong> company. The ingest job never creates
          companies. Creating a new row here is last resort.
        </p>
        <Link
          to="/aug2026-clin-trial-readout-tracker"
          className="text-sm text-indigo-600 hover:underline mt-2 inline-block"
        >
          Open Public Companies Trial Updates
        </Link>
      </div>

      {error ? <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-xl px-4 py-3">{error}</div> : null}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
          </div>
        ) : flags.length === 0 ? (
          <div className="px-5 py-10 text-sm text-slate-500">No unmatched company names. All sponsors mapped to the catalog.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Raw name</th>
                  <th className="px-4 py-2 text-left font-semibold">Where</th>
                  <th className="px-4 py-2 text-left font-semibold">Assign existing company</th>
                  <th className="px-4 py-2 text-left font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {flags.map((flag) => (
                  <tr key={flag.id} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{flag.raw_name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{flag.kind}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {flag.kind === 'source' ? (
                        <div>
                          {flag.source_id}
                          {flag.source_name ? <div className="text-xs text-slate-400">{flag.source_name}</div> : null}
                        </div>
                      ) : (
                        <div>
                          {flag.trial_id}
                          {flag.trial_name ? <div className="text-xs text-slate-400">{flag.trial_name}</div> : null}
                          {flag.nct_id ? <div className="text-xs text-slate-400">{flag.nct_id}</div> : null}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={draft[flag.id] || ''}
                        onChange={(e) => setDraft((prev) => ({ ...prev, [flag.id]: e.target.value }))}
                        className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm w-full max-w-xs"
                      >
                        <option value="">Select company…</option>
                        {companyOptions.map((c) => (
                          <option key={c.company_id} value={c.company_id}>
                            {c.ticker ? `${c.name} (${c.ticker})` : c.name}
                            {flag.suggested_company_id === c.company_id ? ' (suggested)' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={savingId === flag.id || !draft[flag.id]}
                          onClick={() => resolve(flag, { company_id: draft[flag.id] })}
                          className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold disabled:opacity-50"
                        >
                          Assign
                        </button>
                        <button
                          type="button"
                          disabled={savingId === flag.id}
                          onClick={() => resolve(flag, { ignore: true })}
                          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold"
                        >
                          Leave blank
                        </button>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-slate-500 underline"
                        onClick={() =>
                          setCreateOpen((prev) => {
                            const next = !prev[flag.id];
                            if (next) {
                              setCreateName((n) => ({ ...n, [flag.id]: n[flag.id] || flag.raw_name }));
                              setCreateId((n) => ({ ...n, [flag.id]: n[flag.id] || slugify(flag.raw_name) }));
                            }
                            return { ...prev, [flag.id]: next };
                          })
                        }
                      >
                        {createOpen[flag.id] ? 'Hide create' : 'Create new company (last resort)'}
                      </button>
                      {createOpen[flag.id] ? (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 space-y-2">
                          <input
                            value={createName[flag.id] || ''}
                            onChange={(e) => {
                              const name = e.target.value;
                              setCreateName((prev) => ({ ...prev, [flag.id]: name }));
                              setCreateId((prev) => ({ ...prev, [flag.id]: slugify(name) }));
                            }}
                            placeholder="Company name"
                            className="w-full rounded border border-amber-200 px-2 py-1 text-xs"
                          />
                          <input
                            value={createId[flag.id] || ''}
                            onChange={(e) => setCreateId((prev) => ({ ...prev, [flag.id]: e.target.value }))}
                            placeholder="company-id"
                            className="w-full rounded border border-amber-200 px-2 py-1 text-xs font-mono"
                          />
                          <input
                            value={createTicker[flag.id] || ''}
                            onChange={(e) =>
                              setCreateTicker((prev) => ({ ...prev, [flag.id]: e.target.value.toUpperCase() }))
                            }
                            placeholder="Ticker (optional, e.g. RVMD)"
                            className="w-full rounded border border-amber-200 px-2 py-1 text-xs font-mono"
                          />
                          <button
                            type="button"
                            disabled={savingId === flag.id || !createName[flag.id] || !createId[flag.id]}
                            onClick={() =>
                              resolve(flag, {
                                create_company: {
                                  company_id: createId[flag.id],
                                  name: createName[flag.id],
                                  ticker: createTicker[flag.id] || undefined,
                                },
                              })
                            }
                            className="px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
                          >
                            Create and assign
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4">
        <h2 className="text-sm font-bold text-slate-800">Existing companies ({companies.length})</h2>
        <p className="text-xs text-slate-500 mt-1 mb-3">Match names and aliases before creating anything new.</p>
        <div className="flex flex-wrap gap-2">
          {companyOptions.map((c) => (
            <span key={c.company_id} className="rounded-full bg-slate-100 text-slate-700 px-2.5 py-1 text-xs">
              {c.name}
              {c.ticker ? <span className="ml-1 font-mono text-slate-500">{c.ticker}</span> : null}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ManageReadoutCompanies;
